import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
// DMC-D3c-2: canonical deleted-state helper.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
// VIS-A1: stagger the recurring 60s tick by 40s relative to AutoBump
// (offset 0) so this hook anchors the canonical phase-40s slot of the
// 60s polling trio. AutoBump + OptiFinishPushback + this hook all mount
// together from the dashboard, all share a `document.hidden` early-return,
// and previously fired their setIntervals within milliseconds of each
// other on every minute boundary AND on every tab refocus. With this
// stagger their setInterval phases are pinned 40s apart from AutoBump and
// 20s apart from OptiFinishPushback at steady state.
import { createStaggeredInterval, VIS_A1_STAGGER } from '@/utils/staggeredInterval';
// UQA-A2: explicit shop scoping. Pre-A2 the rollover scan below relied
// entirely on RLS to scope per-shop. For multi-shop users (ops staff with
// multi-tenant access in a single session) RLS-scoped row order is
// undefined; the scan could pick up rollovers from every accessible
// shop. Mirrors the OPS-A6 doctrine established for `loadShopHours` in
// OptiPlaceModal:374 — explicit `.eq('shop_id', shopId)` on every
// multi-tenant table query, never RLS-only.
import { useAuth } from '@/contexts/AuthContext';

type SupabaseAny = any;

/**
 * Page-level hook that runs in Dashboard.tsx.
 * Every 60 seconds, checks for paused source tickets that have converted rollover descendants.
 * W4b: when a rollover's growth changed it ASKS the server (`sync_converted_rollover_growth`), which is the one writer
 * of accumulated_rollover_minutes / predicted_minutes; this hook writes nothing itself.
 */
export function useConvertedRolloverGrowth() {
  // VIS-A1: ref now holds the cleanup function returned by
  // createStaggeredInterval (which manages BOTH the deferred startup timer
  // and the eventual setInterval handle). Disposal is a single
  // `cleanupTickRef.current?.()` call regardless of whether the 40s
  // startup delay has fired yet.
  const cleanupTickRef = useRef<(() => void) | null>(null);
  const processingRef = useRef(false);
  // W4b: the server run whose changes this tab already cascaded (one cascade per run, not per tab per minute).
  const lastSeenRunRef = useRef<string>('');
  // UQA-A2: read activeShop from auth so the rollover scan below is
  // shop-scoped rather than RLS-only. See import-block comment for
  // doctrine pointer (OPS-A6 / loadShopHours precedent).
  const { activeShop } = useAuth();

  useEffect(() => {
    async function syncGrowth() {
      // FIX-5 R47: Prevent concurrent ticks from overlapping
      if (processingRef.current) return;
      // V-E-2 R152-MED-2: skip when the tab is hidden. syncGrowth fires a
      // chain (tickets fetch + per-ticket descendants check + canonical
      // propagation/CAS update + cascade trigger) every 60s; running it on
      // background tabs wastes DB hits at multi-tenant scale. The next
      // visible tick will catch up the growth (the function is idempotent
      // — already concurrent-tick-guarded by processingRef).
      if (typeof document !== 'undefined' && document.hidden) return;

      // UQA-A2: short-circuit when no active shop is selected. The
      // subsequent rollover scan + per-ticket descendants checks below
      // ALL require shop scope; without it the scan would either return
      // 0 rows (best case) or rely on RLS for tenant isolation (drift
      // risk for multi-shop users). Mirrors the `loadShopHours`
      // short-circuit pattern established by OPS-A6.
      const shopId = activeShop?.id;
      if (!shopId) return;

      processingRef.current = true;
      try {
        // Find all converted rollover tickets whose source is paused/unresolved
        const { data: rollovers, error: rolloverError } = await (supabase as SupabaseAny)
          .from('tickets')
          .select('id, rollover_of_ticket_id, accumulated_rollover_minutes, predicted_minutes, scheduled_date, shop_id, assigned_tech_id, converted_at, parent_ticket_id')
          .eq('shop_id', shopId)
          .eq('rollover_type', 'converted')
          .in('status', ['Active', 'Paused']);

        if (rolloverError || !rollovers || rollovers.length === 0) return;

        let needsServerSync = false;
        for (const rollover of rollovers) {
          if (!rollover.rollover_of_ticket_id) continue;

          // Get source ticket.
          //
          // `.maybeSingle()` (not `.single()`): `rollover_of_ticket_id` has no
          // FK constraint, so a converted rollover can outlive a hard-deleted
          // or RLS-invisible source. `.single()` sends
          // `Accept: application/vnd.pgrst.object+json`, which makes PostgREST
          // answer 0 rows with HTTP 406 — a red console error every 60s tick
          // for a case the `if (!source) continue` below already handles as
          // normal. `.maybeSingle()` returns `{ data: null }` with a 200.
          //
          // `deleted_at` MUST stay in the projection: `isTicketDeleted` below
          // reads BOTH soft-delete representations (`deleted_at != null ||
          // status === 'Deleted'`). Omitting the column leaves `deleted_at`
          // permanently `undefined`, silently reducing the canonical predicate
          // to its legacy status-only half — a source soft-deleted via the
          // post-DMC-D1 column would keep accruing rollover minutes forever.
          const { data: source } = await (supabase as SupabaseAny)
            .from('tickets')
            .select('id, timer_status, status, paused_at, converted_at, deleted_at')
            .eq('id', rollover.rollover_of_ticket_id)
            .maybeSingle();

          if (!source) continue;

          // Use converted_at from the rollover ticket itself (that's where it's stored)
          const convertedAt = rollover.converted_at || null;
          if (!convertedAt) continue;

          // If source is completed/cancelled, stop growing.
          // DMC-D3c-2: deletion check via canonical helper (both reps).
          if (source.status === 'Completed' || source.status === 'Cancelled' || isTicketDeleted(source)) {
            continue;
          }

          // OCT28 WP-2b-2b-ii: the browser no longer measures pause (it summed pause sessions — parked hours and
          // configured breaks included — and disagreed with the server's ONE eligible-pause measure). A live converted
          // continuation of a paused source is the only question here; the server measures, decides and writes
          // (throttled to one run per shop per minute; pg_cron runs it too).
          if (source.timer_status === 'paused') needsServerSync = true;
        }

        if (needsServerSync) {
          const { data: synced, error: syncError } = await (supabase as SupabaseAny).rpc('sync_converted_rollover_growth', { p_shop: shopId });
          if (syncError) {
            console.error('[useConvertedRolloverGrowth] server growth sync failed:', syncError);
          } else {
            // The server answers what its last run changed (THROTTLED = another tab or the cron ran it moments ago).
            // A live chain's lane is re-walked once per run this tab has not seen yet.
            const runAt = String(synced?.run_at ?? '');
            const changed = Array.isArray(synced?.changed) ? synced.changed as Array<{ scheduled_date?: string; assigned_tech_id?: string | null }> : [];
            if (runAt && runAt !== lastSeenRunRef.current && changed.length > 0) {
              lastSeenRunRef.current = runAt;
              const lanes = new Set(changed.filter((c) => c.scheduled_date).map((c) => `${c.scheduled_date}|${c.assigned_tech_id ?? ''}`));
              for (const lane of lanes) {
                const [date, tech] = lane.split('|');
                triggerBoardCascade(shopId, date, tech || undefined).catch((err) => console.warn('[ConvertedRolloverGrowth] Cascade error:', err));
              }
            }
          }
        }
      } catch (err) {
        // FIX-R132-MED-9: this background sync runs every 60s. If it
        // persistently throws (RLS regression, schema drift), converted
        // rollover tickets stop growing with pause time and the schedule
        // silently drifts. Push to buffer so support sees the failure
        // sequence in the next bug report.
        console.error('[useConvertedRolloverGrowth] sync error:', err);
        try {
          const { pushClientError } = await import('@/utils/clientErrorBuffer');
          pushClientError({
            source: 'mutation',
            message: `[useConvertedRolloverGrowth] sync threw: ${(err as { message?: string } | undefined)?.message ?? String(err)}`,
            stack: (err as { stack?: string } | undefined)?.stack,
            extra: { hook: 'useConvertedRolloverGrowth' },
          });
        } catch {
          // never throw from a logger
        }
      } finally {
        processingRef.current = false;
      }
    }

    // Run immediately on mount (cold-load eagerness preserved)
    syncGrowth();

    // VIS-A1: schedule recurring tick at phase +40s relative to AutoBump
    // (offset 0). Steady-state cycle: AutoBump @ 60/120/180s,
    // OptiFinishPushback @ 80/140/200s, this hook @ 100/160/220s. The
    // trio's three shop_profile + tickets reads + cascade calls now
    // spread across 60s instead of bunching in the same millisecond on
    // every tab refocus.
    cleanupTickRef.current = createStaggeredInterval(
      syncGrowth,
      60_000,
      VIS_A1_STAGGER.CONVERTED_ROLLOVER_GROWTH,
    );

    return () => {
      cleanupTickRef.current?.();
      cleanupTickRef.current = null;
    };
    // UQA-A2: deps now include `activeShop?.id` so the polling interval
    // resets to the new shop's rollover scope when a multi-shop user
    // switches active shop. For single-shop sessions activeShop?.id is
    // stable so the effect is mounted once exactly as before.
  }, [activeShop?.id]);
}
